import { IJobDetailsForm } from '../Types';
import {
  IDraftJobPostEntity,
  IJobPostDetailEntity,
  ISaveJobPostDetailPayload,
} from '~/domain/PermanentJobs/types';
import { PayType, CompensationType } from '~/domain/PermanentJobs/enums';
import { EnumHelper } from '~/utils/EnumHelper';
import { UserSubProfession } from '~/enums/UserProfession';

/**
 * Mapper class for JobDetailsForm data transformations
 * Handles conversions from domain models to form data
 *
 * @example
 * // Convert domain data to form data for editing
 * const formData = JobDetailsFormMapper.fromEntity(draftJobPost);
 */
export class JobDetailsFormMapper {
  /**
   * Converts domain data to form data
   * Always returns complete form data - entity parameter is optional
   *
   * @param draft - Optional domain job post data
   * @returns Complete form data with values merged with defaults
   */
  static fromEntity(entity?: IDraftJobPostEntity | IJobPostDetailEntity): IJobDetailsForm {
    // Business logic: subProfession takes precedence over professionType
    const professionType = entity?.subProfession || entity?.professionType || '';

    // Convert numeric rates to strings for form compatibility
    const minRate = entity?.minRate !== undefined ? String(entity.minRate) : '';
    const maxRate = entity?.maxRate !== undefined ? String(entity.maxRate) : '';

    // Infer compensation type from rates
    // FIXED: minRate == maxRate or only minRate exists
    // RANGE: minRate != maxRate
    const compensationType = this.inferCompensationType(minRate, maxRate);

    return {
      office: entity?.officeSelected ? entity?.officeId : null,
      jobTitle: entity?.title ?? '',
      professionType,
      officeSpecialty: entity?.officeSpecialty ?? '',
      payType: entity?.payType ?? PayType.SALARY,
      compensationType,
      minRate,
      maxRate,
      benefits: entity?.benefits ?? [],
      benefitsOther: entity?.benefitsOther ?? '',
      scheduleType: entity?.scheduleType ?? '',
      expectedDays: entity?.expectedDays ?? [],
      jobDescription: entity?.description ?? '',
    };
  }

  /**
   * Infers compensation type from min and max rates
   * - FIXED: when minRate == maxRate or only minRate exists
   * - RANGE: when minRate != maxRate
   * - Default: FIXED when no rates provided
   */
  private static inferCompensationType(minRate: string, maxRate: string): CompensationType {
    // No rates provided - default to FIXED
    if (!minRate && !maxRate) {
      return CompensationType.FIXED;
    }

    // Only minRate provided - FIXED
    if (minRate && !maxRate) {
      return CompensationType.FIXED;
    }

    // Both rates provided - compare values
    if (minRate && maxRate) {
      const min = parseFloat(minRate.replace(/,/g, ''));
      const max = parseFloat(maxRate.replace(/,/g, ''));

      // Same values - FIXED
      if (min === max) {
        return CompensationType.FIXED;
      }

      // Different values - RANGE
      return CompensationType.RANGE;
    }

    // Default fallback
    return CompensationType.FIXED;
  }

  /**
   * Converts form data to save job post detail payload
   * Transforms form values to API-compatible format
   *
   * @param formData - Form data from JobDetailsForm
   * @returns Payload ready for save job post detail API call
   */
  static toSaveJobPostDetailPayload(formData: IJobDetailsForm): ISaveJobPostDetailPayload {
    // Helper to parse string rates to numbers
    const parseRate = (rate: string): number | undefined => {
      if (!rate) return undefined;
      const parsed = parseFloat(rate.replace(/,/g, ''));
      return isNaN(parsed) ? undefined : parsed;
    };

    const profession = formData.professionType;
    const isSubProfession = EnumHelper.getObjectKeys(UserSubProfession).includes(profession);
    const professionType = isSubProfession
      ? UserSubProfession.getProfession(profession)
      : formData.professionType;
    const subProfession = isSubProfession ? profession : undefined;

    const minRateNum = parseRate(formData.minRate);
    const maxRateNum = parseRate(formData.maxRate);

    // Determine rate fields based on compensationType
    let minRate: number | undefined;
    let maxRate: number | undefined;

    if (formData.compensationType === CompensationType.FIXED) {
      // FIXED compensation: use minRate and maxRate (same value)
      minRate = minRateNum;
      maxRate = minRateNum;
    } else if (formData.compensationType === CompensationType.RANGE) {
      // RANGE compensation: use minRate and maxRate
      minRate = minRateNum;
      maxRate = maxRateNum;
    }

    return {
      benefits: formData.benefits?.length ? formData.benefits : null,
      benefitsOther: formData.benefitsOther || null,
      description: formData.jobDescription || null,
      expectedDays: formData.expectedDays?.length ? formData.expectedDays : null,
      maxRate: maxRate ?? null,
      minRate: minRate ?? null,
      officeId: formData.office || null,
      officeSelected: formData.office ? true : false,
      officeSpecialty: formData.officeSpecialty || null,
      payType: formData.payType || null,
      professionType: professionType || null,
      scheduleType: formData.scheduleType || null,
      subProfession: subProfession || null,
      title: formData.jobTitle || null,
    };
  }
}
